import type { Bts2026Phase2MarketTier } from '../utils/get-back-to-school-2026-phase-2-variant';
import { getBts2026Phase2MarketTier } from '../utils/get-back-to-school-2026-phase-2-variant';

export type BackToSchool2026Phase2Variant = 'g1' | 'g2';

/**
 * Save Now deep link, by market tier (Figma 1944:1243 / 1944:1262). Every phase 2 surface goes
 * straight to checkout, unlike phase 1 which pointed at the MS in AI landing page: this phase is a
 * sitewide sale across courses, MS, and MBA, so there is no single program page to land on.
 * Full-price markets check out on the 4-month bundle; low-price markets on the monthly plan.
 */
export const BTS_2026_P2_CHECKOUT_HREF: Record<Bts2026Phase2MarketTier, string> = {
  full: '/checkout/individual/4month/credit-card',
  low: '/checkout/individual/monthly/credit-card',
};

/**
 * Where the homepage card's "ENDS SOON" row sits. G1 puts it beside the CTA with no divider; G2
 * puts it above the headline with a divider. The asymmetry is intentional in the design — the two
 * tiers are never seen by the same visitor, so they are not normalized.
 */
export type Bts2026Phase2EyebrowPlacement = 'above-headline' | 'beside-cta';

/**
 * Back to School 2026 phase 2 creative, per market tier. A completely different art direction from
 * phase 1: a purple gradient with faint ruled-notebook lines and diagonal pencil art instead of the
 * navy/gold photographic treatment, and no underline art behind the headline. Both phases stay in
 * the registry at once, so this campaign owns its own constants rather than extending phase 1's.
 */
export type BackToSchool2026Phase2VariantConfig = {
  catalogBackgroundGradient: string;
  catalogBackgroundGradientMobile: string;
  catalogBg: string;
  catalogBgMobile: string;
  catalogBorderColor: string;
  catalogCtaColor: string;
  catalogCtaLabel: string;
  catalogCtaTextColor: string;
  catalogHeadlineLine1: string;
  /** Hidden on mobile, where the design leaves only line 1 beside the CTA (Figma 1944:1182). */
  catalogHeadlineLine2: string;
  catalogHeadlineLine2Color: string;
  experienceAtfBg: string;
  experienceAtfCtaLabel: string;
  experienceAtfDescription: string;
  experienceAtfEyebrow: string;
  experienceAtfHeadline: string;
  experienceAtfObjectFit: 'contain' | 'cover';
  experienceAtfObjectPosition: string;
  homepageBackgroundColor: string;
  homepageBg: string;
  homepageBgMobile: string;
  homepageCtaColor: string;
  homepageCtaLabel: string;
  homepageCtaTextColor: string;
  homepageEyebrowColor: string;
  homepageEyebrowLabel: string;
  homepageEyebrowPlacement: Bts2026Phase2EyebrowPlacement;
  homepageHeadlineLine1: string;
  homepageHeadlineLine1Color: string;
  /** Absent for G2, whose headline is a single white line (Figma 1944:1270). */
  homepageHeadlineLine2?: string;
  homepageMobileObjectFit: 'contain' | 'cover';
  homepageMobileObjectPosition: string;
  homepageObjectFit: 'contain' | 'cover';
  homepageObjectPosition: string;
  homepageSubtitle: string;
  homepageSubtitleColor: string;
  homepageTitleColor: string;
};

const BTS_2026_P2_SHARED_CONFIG = {
  catalogBackgroundGradient: 'linear-gradient(101deg, #6100B0 0%, #3A006A 50%)',
  catalogBackgroundGradientMobile: 'linear-gradient(95deg, #6100B0 0%, #3A006A 50%)',
  catalogBg: '/images/promotions/back-to-school-2026-phase-2/catalog-bg.png',
  catalogBgMobile: '/images/promotions/back-to-school-2026-phase-2/catalog-bg-mobile.png',
  catalogBorderColor: '#931CF5',
  catalogCtaColor: '#E5A5FF',
  catalogCtaLabel: 'Save Now',
  catalogCtaTextColor: '#3A006A',
  catalogHeadlineLine2: 'Build future-ready skills for your next career move.',
  catalogHeadlineLine2Color: '#EBBAFF',
  experienceAtfBg: '/images/promotions/back-to-school-2026-phase-2/experience-atf-bg.png',
  experienceAtfCtaLabel: 'Save Now',
  experienceAtfDescription: 'Courses, MS and MBA programs built for your next career move.',
  experienceAtfEyebrow: 'ENDS SOON',
  experienceAtfObjectFit: 'cover',
  experienceAtfObjectPosition: 'center',
  homepageBackgroundColor: '#0A0B0F',
  homepageBg: '/images/promotions/back-to-school-2026-phase-2/homepage-bg.png',
  homepageBgMobile: '/images/promotions/back-to-school-2026-phase-2/homepage-bg-mobile.png',
  homepageCtaColor: '#E5A5FF',
  homepageCtaLabel: 'Save Now',
  homepageCtaTextColor: '#3A006A',
  homepageMobileObjectFit: 'cover',
  homepageMobileObjectPosition: 'right top',
  homepageObjectFit: 'cover',
  homepageObjectPosition: 'right center',
  homepageSubtitle: 'Build future-ready skills with the latest AI and tech courses, MS and MBA programs.',
  homepageSubtitleColor: '#F6DFFF',
  homepageTitleColor: 'white',
} as const satisfies Partial<BackToSchool2026Phase2VariantConfig>;

export const BTS_2026_P2_VARIANTS: Record<BackToSchool2026Phase2Variant, BackToSchool2026Phase2VariantConfig> = {
  g1: {
    ...BTS_2026_P2_SHARED_CONFIG,
    catalogHeadlineLine1: 'Back to school sale: 50% OFF',
    experienceAtfHeadline: 'Back to school sale: 50% OFF',
    homepageEyebrowColor: 'white',
    homepageEyebrowLabel: 'Ends soon!',
    homepageEyebrowPlacement: 'beside-cta',
    homepageHeadlineLine1: 'Back to school sale',
    homepageHeadlineLine1Color: '#E5A5FF',
    homepageHeadlineLine2: '50% off your next career move',
  },
  g2: {
    ...BTS_2026_P2_SHARED_CONFIG,
    catalogHeadlineLine1: 'Back to school sale: sitewide',
    experienceAtfHeadline: 'Back to school sale: sitewide',
    homepageEyebrowColor: '#EBBAFF',
    homepageEyebrowLabel: 'Ends soon',
    homepageEyebrowPlacement: 'above-headline',
    homepageHeadlineLine1: 'Back to school sale sitewide',
    homepageHeadlineLine1Color: 'white',
  },
};

/**
 * Variant checkout link with the live coupon appended as `?coupon=<promoCode>`. The coupon also
 * auto-applies from the `COUPON_COOKIE` that `usePromotion` writes; the query param is a fallback
 * in case auto-apply does not run. When absent, the bare tier link is returned.
 */
export function getBts2026Phase2CheckoutHref(
  variant: BackToSchool2026Phase2Variant,
  promoCode?: string | null
): string {
  const href = BTS_2026_P2_CHECKOUT_HREF[getBts2026Phase2MarketTier(variant)];
  if (!promoCode) {
    return href;
  }
  const separator = href.includes('?') ? '&' : '?';
  return `${href}${separator}coupon=${encodeURIComponent(promoCode)}`;
}
